/**
 * web/keyboard.ts
 * Pure-DOM keyboard listener attach/detach.
 *
 * Reuses `keyBindings`, `inputState`, and `clearAllInputState` from
 * the engine so the key-to-input-state mapping has a single source of truth.
 *
 * @module web/keyboard
 */

/// <reference lib="dom" />

import { inputState, clearAllInputState } from '@src/game/input/inputState';
import { keyBindings, capturingKey } from '@src/game/input/keyBindings';

const heldKeys = new Set<string>();

const allReleased = (codes: string[]): boolean =>
  codes.every((c) => !heldKeys.has(c));

const isTextInputTarget = (e: KeyboardEvent): boolean => {
  const el = e.target;
  if (!(el instanceof Element)) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'select' || tag === 'textarea';
};

const isButtonTarget = (e: KeyboardEvent): boolean => {
  const el = e.target;
  if (!(el instanceof Element)) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'button' || tag === 'a') return true;
  const role = el.getAttribute('role');
  return role === 'button' || role === 'link' || role === 'menuitem' ||
    role === 'option' || role === 'combobox';
};

/** Clear all held inputState flags + the local heldKeys cache. */
export function clearAllHeld(): void {
  clearAllInputState();
  heldKeys.clear();
}

const handleKeyDown = (e: KeyboardEvent): void => {
  if (capturingKey) return;
  if (isTextInputTarget(e)) return;
  if (isButtonTarget(e)) {
    if (e.code === 'Space' || e.code === 'Enter') return;
    if (e.target instanceof HTMLElement) e.target.blur();
  }

  const allBound = [
    ...keyBindings.left, ...keyBindings.right,
    ...keyBindings.down, ...keyBindings.up,
    ...keyBindings.jump, ...keyBindings.attack,
  ];
  if (allBound.includes(e.code)) e.preventDefault();

  if (keyBindings.left.includes(e.code)) inputState.left = true;
  if (keyBindings.right.includes(e.code)) inputState.right = true;
  if (keyBindings.down.includes(e.code)) inputState.down = true;
  if (keyBindings.up.includes(e.code)) inputState.up = true;
  if (keyBindings.jump.includes(e.code)) {
    if (!inputState.jumpHeld) inputState.jumpPressed = true;
    inputState.jumpHeld = true;
  }
  if (keyBindings.attack.includes(e.code)) {
    if (!inputState.attackHeld) inputState.attackPressed = true;
    inputState.attackHeld = true;
  }
  heldKeys.add(e.code);
};

const handleKeyUp = (e: KeyboardEvent): void => {
  heldKeys.delete(e.code);
  if (keyBindings.left.includes(e.code) && allReleased(keyBindings.left)) inputState.left = false;
  if (keyBindings.right.includes(e.code) && allReleased(keyBindings.right)) inputState.right = false;
  if (keyBindings.down.includes(e.code) && allReleased(keyBindings.down)) inputState.down = false;
  if (keyBindings.up.includes(e.code) && allReleased(keyBindings.up)) inputState.up = false;
  if (keyBindings.jump.includes(e.code) && allReleased(keyBindings.jump)) inputState.jumpHeld = false;
  if (keyBindings.attack.includes(e.code) && allReleased(keyBindings.attack)) inputState.attackHeld = false;
};

/**
 * Attach the keyboard listeners to the browser window. Returns a detach
 * function that removes the listeners and clears any held state.
 */
export function attachKeyboardListeners(): () => void {
  const win = globalThis as unknown as Window;
  win.addEventListener('keydown', handleKeyDown);
  win.addEventListener('keyup', handleKeyUp);
  win.addEventListener('blur', clearAllHeld);
  return (): void => {
    win.removeEventListener('keydown', handleKeyDown);
    win.removeEventListener('keyup', handleKeyUp);
    win.removeEventListener('blur', clearAllHeld);
    clearAllHeld();
  };
}
